// Pure positioning helper for trigger-anchored popups (dropdowns,
// dialogs-mounted-to-Portal, settings popovers). Returns the {top, left}
// pixel coordinates for a popup that should:
//
//   1. sit just below the trigger (preferred), OR
//   2. flip above the trigger when there isn't enough room below AND
//      there IS more room above (avoids cropping at the viewport bottom),
//   3. with the left edge clamped so the popup doesn't shoot off the
//      right edge of the viewport, and never goes < margin from any
//      edge.
//
// `width` is returned alongside so consumers can apply it to the popup
// style in one go — useful for the "popup matches trigger width" pattern
// (Dropdown, SearchableSelect, Combobox).
//
// All inputs are plain numbers / rects so this helper is trivially
// unit-testable without a DOM. Use the matching `flipPopupPositionFromEl`
// wrapper when you're working with real DOM elements.

export interface FlipPopupInput {
  /** Trigger element rect in viewport coords (typically from getBoundingClientRect). */
  trigger: { top: number; bottom: number; left: number; right: number; width: number };
  /** Popup width in pixels. */
  popupWidth: number;
  /** Popup height in pixels — pass the measured rect height once it's
   *  in the DOM, or a worst-case estimate for the first paint. */
  popupHeight: number;
  /** Viewport dimensions (window.innerWidth / innerHeight at the time of measurement). */
  viewport: { width: number; height: number };
  /** Px gap between the popup and the trigger / viewport edges. */
  margin?: number | undefined;
}

export interface FlipPopupOutput {
  top: number;
  left: number;
  width: number;
}

export function flipPopupPosition(input: FlipPopupInput): FlipPopupOutput {
  const margin = input.margin ?? 4;
  const { trigger, popupWidth, popupHeight, viewport } = input;
  const spaceBelow = viewport.height - trigger.bottom - margin;
  const spaceAbove = trigger.top - margin;
  // Default: just below the trigger.
  let top = trigger.bottom + margin;
  // Flip above ONLY when the popup doesn't fit below AND there's more
  // room above. Just-doesn't-fit-below but ALSO doesn't-fit-above stays
  // below — clipping at the bottom is preferable to flipping into an
  // even worse spot.
  if (popupHeight > spaceBelow && spaceAbove > spaceBelow) {
    top = Math.max(margin, trigger.top - popupHeight - margin);
  }
  let left = trigger.left;
  if (left + popupWidth > viewport.width - margin) {
    left = Math.max(margin, viewport.width - popupWidth - margin);
  }
  return { top, left, width: popupWidth };
}

// Thin convenience wrapper that reads the DOM rects + viewport size and
// hands them to the pure helper. Use this from a Solid effect / ref
// callback. `popupEl` is optional — when omitted (first paint, before
// the popup is in the DOM), `estimatedHeight` is used so the initial
// position already lands at the right end of the trigger.
export interface FlipPopupForElementInput {
  trigger: HTMLElement;
  popup?: HTMLElement | null | undefined;
  /** Width override (Dropdown-style popups want to match the trigger
   *  width; column-filter popovers want a fixed minimum unrelated to
   *  the trigger). Defaults to trigger.width. */
  width?: number | undefined;
  /** Worst-case popup height for the pre-mount paint. */
  estimatedHeight?: number | undefined;
  margin?: number | undefined;
}

export function flipPopupPositionForElement(input: FlipPopupForElementInput): FlipPopupOutput {
  const r = input.trigger.getBoundingClientRect();
  const popupRect = input.popup?.getBoundingClientRect();
  const popupWidth = popupRect?.width ?? input.width ?? r.width;
  const popupHeight = popupRect?.height ?? input.estimatedHeight ?? 420;
  return flipPopupPosition({
    trigger: { top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width },
    popupWidth,
    popupHeight,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    margin: input.margin,
  });
}
